import { zodResolver } from '@hookform/resolvers/zod';
import { subYears } from 'date-fns';
import { t } from 'i18next';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { ScrollView as ScrollViewType } from 'react-native';
import { ScrollView, View } from 'react-native';
import Modal from 'react-native-modal';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';
import { z } from 'zod';

import { useCustomer } from '@/api/customers/use-customer';
import { useCustomerStartOtp } from '@/api/customers/use-customer-start-otp';
import { useCustomerVerifyOtp } from '@/api/customers/use-customer-verify-otp';
import { InputPhone } from '@/components/input-phone';
import { ExistingPhoneDisplay } from '@/components/input-phone/existing-phone-display';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { SmsConsentDisclaimer } from '@/components/sms-consent-disclaimer';
import mixpanelService from '@/core/clients/mixpanel';
import type { FinishProfileFormType } from '@/core/hooks/use-finish-profile-form';
import { useFinishProfileForm } from '@/core/hooks/use-finish-profile-form';
import { Button, ControlledInput, showErrorMessage, Text } from '@/ui';
import { Loader } from '@/ui/loader';

const RESEND_COOLDOWN = 60;
const OTP_LENGTH = 6;

const otpFormSchema = z.object({ code: z.string().length(OTP_LENGTH) });
type OtpFormType = z.infer<typeof otpFormSchema>;

interface FinishProfileModalProps {
  visible: boolean;
  onClose: () => void;
  onDone: () => void;
}

type Step = 'profile' | 'otp';

export function FinishProfileModal({
  visible,
  onClose,
  onDone,
}: FinishProfileModalProps) {
  const { styles } = useStyles(stylesheet);
  const [step, setStep] = useState<Step>('profile');
  const [phone, setPhone] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [resendTimer, setResendTimer] = useState(RESEND_COOLDOWN);
  const profileScrollRef = useRef<ScrollViewType>(null);

  const { refetch: refetchCustomer } = useCustomer();
  const { mutate: verifyCustomerOtp, isPending: isVerifyingOtp } =
    useCustomerVerifyOtp();
  const { mutate: startCustomerOtp, isPending: isResendingOtp } =
    useCustomerStartOtp();

  const {
    control: profileControl,
    trigger,
    setValue,
    handleSubmit: handleProfileFormSubmit,
    isValid: isProfileValid,
    handleDobChange,
    setPhone: setUnverifiedPhone,
    setCountry,
    phoneKnown,
    parsedExistingPhone,
    isValidPhoneNumber,
    isSaving,
    submitProfile,
  } = useFinishProfileForm();

  const {
    handleSubmit: handleOtpFormSubmit,
    control: otpControl,
    setValue: setOtpValue,
    trigger: triggerOtp,
    setError: setOtpError,
    clearErrors: clearOtpErrors,
    formState: { isValid: isOtpValid },
  } = useForm<OtpFormType>({ resolver: zodResolver(otpFormSchema) });

  useEffect(() => {
    if (visible) {
      mixpanelService.track('Page Viewed', {
        'Page Name': 'Finish Profile Modal',
      });
      setStep('profile');
    }
  }, [visible]);

  useEffect(() => {
    if (step !== 'otp' || resendTimer <= 0) return;

    const timeout = setTimeout(
      () => setResendTimer((current) => current - 1),
      1000
    );

    return () => clearTimeout(timeout);
  }, [step, resendTimer]);

  const handleProfileContinue = (data: FinishProfileFormType) => {
    mixpanelService.track('Button Clicked', {
      'Button Name': 'Finish Profile Modal Continue',
    });

    setSubmitError(null);

    submitProfile(data, {
      // The opt-in checkbox is gone: submitting contact information is the
      // consent, per the disclaimer shown alongside these fields.
      smsOptIn: true,
      onDone: (newPhone) => {
        if (!newPhone) {
          refetchCustomer().then(onDone);
          return;
        }

        setPhone(newPhone);
        setResendTimer(RESEND_COOLDOWN);
        setStep('otp');
      },
      onError: setSubmitError,
    });
  };

  const handleOtpContinue = (data: OtpFormType) => {
    mixpanelService.track('Button Clicked', {
      'Button Name': 'Finish Profile Modal Verify Otp',
    });

    verifyCustomerOtp(
      { phone, otp: data.code },
      {
        onSuccess: async () => {
          await refetchCustomer();
          onDone();
        },
        onError: (e) =>
          setOtpError('code', { type: 'manual', message: e.message }),
      }
    );
  };

  const handleResendOtp = () => {
    if (resendTimer > 0) return;

    startCustomerOtp(
      { phone },
      {
        onSuccess: () => setResendTimer(RESEND_COOLDOWN),
        onError: (e) => showErrorMessage(e.message),
      }
    );
  };

  const isLoading = isSaving || isVerifyingOtp || isResendingOtp;
  const resendTimerLabel = `${Math.floor(resendTimer / 60)}:${String(
    resendTimer % 60
  ).padStart(2, '0')}`;

  return (
    <Modal
      animationIn="fadeIn"
      animationInTiming={200}
      animationOut="fadeOut"
      animationOutTiming={200}
      isVisible={visible}
      hasBackdrop
      avoidKeyboard
      onBackdropPress={onClose}
      style={styles.modal}
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <Text
            type="title2"
            style={styles.headerTitle}
            tx={
              step === 'profile'
                ? 'checkout.finishProfileTitle'
                : 'entry.otp.title'
            }
          />

          <NavigationButton
            type={NavigationButtonTypes.cancel}
            onPress={onClose}
          />
        </View>

        {isLoading ? (
          <View style={styles.loaderContainer}>
            <Loader style={{ width: 100, height: 100 }} />
          </View>
        ) : step === 'profile' ? (
          <React.Fragment key="profile">
            <ScrollView
              ref={profileScrollRef}
              keyboardShouldPersistTaps="handled"
              style={styles.body}
              contentContainerStyle={{ gap: 16, paddingBottom: 16 }}
            >
              <View style={styles.nameRow}>
                <ControlledInput
                  control={profileControl}
                  name="firstName"
                  label="First name"
                  textContainerStyle={styles.inputFill}
                  onBlur={() => trigger('firstName')}
                  onChangeText={(text) => {
                    setValue('firstName', text);
                    trigger('firstName');
                  }}
                  onSubmitEditing={handleProfileFormSubmit(
                    handleProfileContinue
                  )}
                />
                <ControlledInput
                  control={profileControl}
                  name="lastName"
                  label="Last name"
                  textContainerStyle={styles.inputFill}
                  onBlur={() => trigger('lastName')}
                  onChangeText={(text) => {
                    setValue('lastName', text);
                    trigger('lastName');
                  }}
                  onSubmitEditing={handleProfileFormSubmit(
                    handleProfileContinue
                  )}
                />
              </View>

              <ControlledInput
                control={profileControl}
                name="email"
                label="Email"
                textContainerStyle={styles.inputFill}
                spellCheck={false}
                onBlur={() => trigger('email')}
                onChangeText={(text) => {
                  setValue('email', text);
                  trigger('email');
                }}
                autoComplete="email"
                onSubmitEditing={handleProfileFormSubmit(handleProfileContinue)}
              />

              <ControlledInput
                control={profileControl}
                name="dob"
                label="Date of birth - MM/DD/YYYY"
                type="date"
                keyboardType="number-pad"
                inputMode="numeric"
                textContainerStyle={styles.inputFill}
                onBlur={() => trigger('dob')}
                onChangeText={handleDobChange}
                maximumDate={subYears(new Date(), 21)}
                defaultDate={subYears(new Date(), 25)}
                onSubmitEditing={handleProfileFormSubmit(handleProfileContinue)}
              />

              {phoneKnown ? (
                <ExistingPhoneDisplay phone={parsedExistingPhone} />
              ) : (
                <InputPhone
                  onChangeText={setUnverifiedPhone}
                  onCountryChange={setCountry}
                  textContainerStyle={styles.inputFill}
                  onFocus={() =>
                    setTimeout(
                      () =>
                        profileScrollRef.current?.scrollToEnd({
                          animated: true,
                        }),
                      100
                    )
                  }
                  placeholder={t('entry.phonePlaceholder')}
                  onSubmitEditing={handleProfileFormSubmit(
                    handleProfileContinue
                  )}
                />
              )}

              <SmsConsentDisclaimer />
            </ScrollView>

            <View style={styles.footer}>
              <Button
                tx="entry.finishSignup.continue"
                type="primary"
                onPress={() => {
                  // Reset any prior error on each attempt so a stale message
                  // never lingers after a retry.
                  setSubmitError(null);
                  handleProfileFormSubmit(handleProfileContinue)();
                }}
                disabled={!isProfileValid || !isValidPhoneNumber}
                style={{ width: 'auto', minWidth: 150 }}
              />

              {submitError ? (
                <Text type="caption1" style={styles.footerError}>
                  {submitError}
                </Text>
              ) : null}
            </View>
          </React.Fragment>
        ) : (
          <React.Fragment key="otp">
            <ScrollView
              keyboardShouldPersistTaps="handled"
              style={styles.body}
              contentContainerStyle={{ gap: 16 }}
            >
              <Text type="bodyRegular" style={styles.cardSubtitle}>
                {t('entry.otp.bodyPrefix', { length: OTP_LENGTH })}
                <Text type="bodyRegular" style={styles.phoneValue}>
                  {`${phone}.`}
                </Text>
              </Text>

              <ControlledInput
                control={otpControl}
                name="code"
                label={t('entry.otp.codeLabel')}
                placeholder={t('entry.otp.codePlaceholder')}
                keyboardType="number-pad"
                textContainerStyle={styles.inputFill}
                onChangeText={(text) => {
                  clearOtpErrors('code');
                  setOtpValue('code', text);
                  triggerOtp('code');
                }}
                onSubmitEditing={handleOtpFormSubmit(handleOtpContinue)}
              />

              {resendTimer <= 0 ? (
                <Text type="caption1" style={styles.cardSubtitle}>
                  {t('entry.otp.resendPrompt')}
                  <Text
                    type="caption1"
                    style={[styles.cardSubtitle, styles.resendLink]}
                    onPress={handleResendOtp}
                  >
                    {t('entry.otp.resendLink')}
                  </Text>
                </Text>
              ) : (
                <Text
                  tx="entry.otp.resendCountdown"
                  type="caption1"
                  style={styles.cardSubtitle}
                  values={{ time: resendTimerLabel }}
                />
              )}
            </ScrollView>

            <View style={styles.footer}>
              <Button
                tx="entry.otp.verifyCode"
                type="primary"
                onPress={handleOtpFormSubmit(handleOtpContinue)}
                disabled={!isOtpValid}
                style={{ width: 'auto', minWidth: 150 }}
              />
            </View>
          </React.Fragment>
        )}
      </View>
    </Modal>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  modal: {
    margin: 0,
    alignItems: 'center',
    justifyContent: 'center',
    width: { xs: '95%', md: 568 },
    alignSelf: 'center',
  },
  container: {
    width: '100%',
    // The modal is vertically centered, so a plain percentage height can still
    // butt up against the dynamic island / status bar on tall phones. On mobile
    // cap the height to the screen minus twice the largest safe-area inset (the
    // centering splits the leftover evenly), so both edges clear the notch;
    // the body scrolls for anything that doesn't fit. Desktop keeps 90%.
    maxHeight: {
      xs:
        UnistylesRuntime.screen.height -
        2 *
          Math.max(
            UnistylesRuntime.insets.top,
            UnistylesRuntime.insets.bottom
          ) -
        24,
      md: '90%',
    },
    borderRadius: 16,
    borderWidth: 1,
    borderColor: theme.colors.dialog.borderColor,
    backgroundColor: theme.colors.dialog.backgroundColor,
    gap: 24,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  headerTitle: {
    flex: 1,
    flexShrink: 1,
    maxWidth: { xs: 275, md: '100%' },
  },
  loaderContainer: {
    paddingVertical: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    paddingHorizontal: 16,
  },
  nameRow: {
    flexDirection: 'column',
    gap: 8,
  },
  inputFill: {
    backgroundColor: theme.colors.background_1,
  },
  cardSubtitle: {
    color: theme.colors.text.secondary,
  },
  phoneValue: {
    color: theme.colors.text.primary,
  },
  resendLink: {
    textDecorationLine: 'underline',
  },
  footer: {
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 8,
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: theme.colors.card.default.borderColor,
  },
  footerError: {
    color: theme.colors.input.error.helperText,
    textAlign: 'right',
  },
}));
